import type { InstanceConfig } from './config.ts';

/**
 * Hand-rolled URL handling. `URL` is a WHATWG global, not ES2022 — it does not exist under R1's
 * `lib: ["ES2022"], types: []`, and it is banned from models anyway (R3). Everything here is
 * total: bad input yields `null`, never a throw.
 */
export interface UrlParts {
  readonly absolute: boolean;
  /** Lowercase, no trailing colon. `''` when relative. */
  readonly scheme: string;
  /** Lowercase, default port removed. `''` when relative. */
  readonly host: string;
  /** `''` or starts with `/`. Percent-encoding is preserved verbatim. */
  readonly path: string;
  /** Without the leading `?`. */
  readonly query: string;
  /** Without the leading `#`. */
  readonly fragment: string;
}

const ABSOLUTE_RE = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/([^/?#]*)([^?#]*)(?:\?([^#]*))?(?:#([\s\S]*))?$/;
const RELATIVE_RE = /^([^?#]*)(?:\?([^#]*))?(?:#([\s\S]*))?$/;

function stripDefaultPort(host: string, scheme: string): string {
  if (scheme === 'https' && host.endsWith(':443')) return host.slice(0, -4);
  if (scheme === 'http' && host.endsWith(':80')) return host.slice(0, -3);
  return host;
}

/**
 * Parses an absolute or relative URL. Protocol-relative (`//host/x`) is **rejected** — a scheme-less
 * authority has no meaning without a page to inherit from, and core has no page (R1).
 *
 * It is not hypothetical input, though: `authorThumbnails[].url` arrives in that form on some
 * search records (§2). {@link rewriteAssetUrl} normalises it to `https:` before calling this, which
 * keeps the decision in one place instead of letting every caller guess a scheme.
 */
export function parseUrl(input: string): UrlParts | null {
  const raw = input.trim();
  if (raw === '') return null;

  const absolute = ABSOLUTE_RE.exec(raw);
  if (absolute) {
    const scheme = (absolute[1] ?? '').toLowerCase();
    const host = stripDefaultPort((absolute[2] ?? '').toLowerCase(), scheme);
    if (host === '') return null;
    return {
      absolute: true,
      scheme,
      host,
      path: absolute[3] ?? '',
      query: absolute[4] ?? '',
      fragment: absolute[5] ?? '',
    };
  }

  if (raw.startsWith('//')) return null;
  const relative = RELATIVE_RE.exec(raw);
  if (!relative) return null;
  return {
    absolute: false,
    scheme: '',
    host: '',
    path: relative[1] ?? '',
    query: relative[2] ?? '',
    fragment: relative[3] ?? '',
  };
}

export function formatUrl(parts: UrlParts): string {
  const base = parts.absolute ? `${parts.scheme}://${parts.host}${parts.path}` : parts.path;
  const query = parts.query === '' ? '' : `?${parts.query}`;
  const fragment = parts.fragment === '' ? '' : `#${parts.fragment}`;
  return `${base}${query}${fragment}`;
}

/** `https://host[:port]`, or `null` for a relative URL. */
export function originOf(input: string): string | null {
  const parts = parseUrl(input);
  if (!parts || !parts.absolute) return null;
  return `${parts.scheme}://${parts.host}`;
}

function trimTrailingSlashes(path: string): string {
  let end = path.length;
  while (end > 0 && path.charAt(end - 1) === '/') end -= 1;
  return path.slice(0, end);
}

function ensureLeadingSlash(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

/** Removes `prefix` from the front of `path`, respecting segment boundaries. */
function stripPathPrefix(path: string, prefix: string): string {
  const base = trimTrailingSlashes(prefix);
  if (base === '' || base === '/') return path;
  if (path === base) return '';
  if (path.startsWith(`${base}/`)) return path.slice(base.length);
  return path;
}

function joinOnto(baseUrl: string, path: string, query: string, fragment: string): string | null {
  const base = parseUrl(baseUrl);
  if (!base || !base.absolute) return null;
  return formatUrl({
    ...base,
    path: `${trimTrailingSlashes(base.path)}${path === '' ? '' : ensureLeadingSlash(path)}`,
    query,
    fragment,
  });
}

/**
 * R2 — the *only* way an instance-space URL becomes a fetchable one.
 *
 * Accepts a relative path (`/api/v1/captions/…`) or an absolute URL already pointing at the
 * instance (`dashUrl`, `hlsUrl`, `videoThumbnails[].url`), strips the `instanceUrl` origin (and
 * base path) off the latter, and re-joins onto `transportBaseUrl`. Handing Shaka a raw absolute
 * `dashUrl` would bypass transport entirely.
 *
 * A URL on a *foreign* origin returns `null` rather than passing through: that is what keeps
 * "no instance-origin absolute URL escapes core except via links/" true by construction. Google
 * asset hosts have their own door — {@link rewriteAssetUrl}.
 */
export function resolveInstanceUrl(config: InstanceConfig, input: string): string | null {
  const parts = parseUrl(input);
  if (!parts) return null;

  if (!parts.absolute) {
    return joinOnto(config.transportBaseUrl, parts.path, parts.query, parts.fragment);
  }

  const origin = `${parts.scheme}://${parts.host}`;
  const instance = parseUrl(config.instanceUrl);
  const transport = parseUrl(config.transportBaseUrl);
  if (!instance || !transport) return null;

  let path: string;
  if (origin === `${instance.scheme}://${instance.host}`) {
    path = stripPathPrefix(parts.path, instance.path);
  } else if (origin === `${transport.scheme}://${transport.host}`) {
    path = stripPathPrefix(parts.path, transport.path);
  } else {
    return null;
  }

  return joinOnto(config.transportBaseUrl, path, parts.query, parts.fragment);
}

/**
 * Joins a relative path (with its query and fragment) onto any absolute base URL, base path and
 * all. Returns `null` for a base that is not absolute or an input that already is — "join this
 * absolute URL onto that origin" has no meaning, and guessing one is how a link ends up pointing at
 * two hosts at once.
 *
 * Exported for `links/external.ts`, which needs the same join against a *foreign* origin
 * (`https://www.youtube.com`); {@link resolveDisplayUrl} is the instance-scoped case of it.
 */
export function joinUrl(baseUrl: string, input: string): string | null {
  const parts = parseUrl(input);
  if (!parts || parts.absolute) return null;
  return joinOnto(baseUrl, parts.path, parts.query, parts.fragment);
}

/**
 * The display counterpart: builds a URL on `instanceUrl` for links the user follows out of the
 * app (`links/`). Never use this to fetch — that is {@link resolveInstanceUrl}'s job.
 */
export function resolveDisplayUrl(config: InstanceConfig, input: string): string | null {
  const parts = parseUrl(input);
  if (!parts) return null;
  if (parts.absolute) {
    const instance = parseUrl(config.instanceUrl);
    if (!instance) return null;
    if (`${parts.scheme}://${parts.host}` !== `${instance.scheme}://${instance.host}`) return null;
    return formatUrl(parts);
  }
  return joinOnto(config.instanceUrl, parts.path, parts.query, parts.fragment);
}

export type QueryValue = string | number | boolean | null | undefined;

function splitQuery(query: string): { key: string; pair: string }[] {
  if (query === '') return [];
  const out: { key: string; pair: string }[] = [];
  for (const pair of query.split('&')) {
    if (pair === '') continue;
    const eq = pair.indexOf('=');
    out.push({ key: eq === -1 ? pair : pair.slice(0, eq), pair });
  }
  return out;
}

/**
 * R2 — needed because `hlsUrl` already carries a query, so `?local=true` cannot be string-concatenated.
 *
 * Existing pairs are preserved byte-for-byte (they are already encoded, and re-encoding a signed
 * media URL breaks the signature); a key present in `params` replaces the existing pair in place.
 * `null`/`undefined` removes a key. The fragment is preserved — storyboard cue URLs carry `#xywh=`.
 */
export function withQuery(url: string, params: Readonly<Record<string, QueryValue>>): string | null {
  const parts = parseUrl(url);
  if (!parts) return null;

  const existing = splitQuery(parts.query);
  const applied: string[] = [];
  const kept: string[] = [];

  for (const { key, pair } of existing) {
    if (!Object.prototype.hasOwnProperty.call(params, key)) {
      kept.push(pair);
      continue;
    }
    const value = params[key];
    if (value === null || value === undefined) continue;
    kept.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    applied.push(key);
  }

  for (const key of Object.keys(params)) {
    if (applied.includes(key)) continue;
    const value = params[key];
    if (value === null || value === undefined) continue;
    kept.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }

  return formatUrl({ ...parts, query: kept.join('&') });
}

/** Reads a single query parameter's decoded value. */
export function queryParam(url: string, key: string): string | null {
  const parts = parseUrl(url);
  if (!parts) return null;
  for (const { key: candidate, pair } of splitQuery(parts.query)) {
    if (candidate !== key) continue;
    const eq = pair.indexOf('=');
    const raw = eq === -1 ? '' : pair.slice(eq + 1);
    try {
      return decodeURIComponent(raw.replace(/\+/g, ' '));
    } catch {
      return raw;
    }
  }
  return null;
}

/**
 * Reads a single `/key/value/` pair out of the path, decoded.
 *
 * YouTube's HLS URLs carry the *same* fields as googlevideo's query string but encode them as path
 * segments: `/api/manifest/hls_playlist/expire/1785175375/…/itag/96/…/playlist/index.m3u8`. Values
 * are percent-encoded inside the segment (`/xtags/lang%3Den/`, `/mime/text%2Fvtt/`), so this
 * decodes exactly as {@link queryParam} does.
 *
 * Pairs are read from the **left**, which is what makes the last segment (`/playlist/index.m3u8`)
 * harmless: it is a well-formed pair whose key nothing asks for.
 */
export function pathParam(url: string, key: string): string | null {
  const parts = parseUrl(url);
  if (!parts) return null;

  const segments = parts.path.split('/');
  for (let i = 0; i < segments.length - 1; i += 1) {
    if (segments[i] !== key) continue;
    const raw = segments[i + 1] ?? '';
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
}

/**
 * googlevideo carries `expire` as a query parameter; the HLS manifest URLs carry the same fields
 * as *path segments* (`/expire/1785175375/ei/…`). Both shapes, one function.
 */
export function parseExpiryEpochSec(url: string): number | null {
  const fromQuery = queryParam(url, 'expire');
  if (fromQuery !== null && /^\d+$/.test(fromQuery)) return Number(fromQuery);

  const fromPath = pathParam(url, 'expire');
  if (fromPath !== null && /^\d+$/.test(fromPath)) return Number(fromPath);

  return null;
}

/**
 * R9 — every error path redacts before the URL reaches a log or an error object. `pot`, `sig`,
 * `n`, `ip` and `spc` are the ones that actually leak (session tokens, signatures, the instance's
 * egress IP); `lsig` and `bui` are the same class of value under different names. Handles both the
 * query-parameter and the HLS path-segment shape.
 */
const REDACTED_KEYS = ['pot', 'sig', 'lsig', 'n', 'ip', 'spc', 'bui', 'token'];
const REDACTION = 'REDACTED';

export function redactUrl(url: string): string {
  const parts = parseUrl(url);
  if (!parts) return REDACTION;

  const query = splitQuery(parts.query)
    .map(({ key, pair }) => (REDACTED_KEYS.includes(key) ? `${key}=${REDACTION}` : pair))
    .join('&');

  const segments = parts.path.split('/');
  for (let i = 0; i < segments.length - 1; i += 1) {
    const key = segments[i];
    if (key !== undefined && REDACTED_KEYS.includes(key)) segments[i + 1] = REDACTION;
  }

  return formatUrl({ ...parts, path: segments.join('/'), query, fragment: '' });
}
